import { CheckOutlined, DownOutlined } from '@ant-design/icons'
import { Button, Col, Form, Input, notification, Row, Select, Spin } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import {
  useGetInfoByUserCode,
  useUpdateCompanyUser,
} from '../../../../../api/company'
import { buttonStyle } from '../../../../../components/pages/auth/components/styles'
import {
  cityRules,
  clearFieldErrorMessageOnChange,
  companyNameRules,
  directorNameRules,
  emailRules,
  nameRules,
  onlyLatinCharsDigitsRule,
  phoneNumberRules,
  postalCodeRules,
  tinRules,
  websiteRules,
} from '../../../../../utils/form.constants'
import { useMetadataCountries } from '../../../../../utils/hooks/useMetadataCountries'
import { companyRegistrationNumberRules } from '../../../../payment-request/addCompany.constants'

const { TextArea } = Input

interface IDetailInfoProps {
  tabActive: 'info' | 'bank' | 'wallet'
  id: string | undefined
  detailCompany: any
  isLoadingDetail: boolean
  handleChangeTab: (value: string) => void
  isActive?: boolean
  tabList: {
    name: string
    value: string
  }[]
}

const requiredFields = [
  'companyName',
  'nickName',
  'directorFirstName',
  'directorLastName',
  'address1',
  'city',
  'stateProvince',
  'companyOfficiallyRegistered',
  'tinVatUid',
  'regNo',
  'phoneNumber1',
  'email',
  'website',
]

const DetailInfo = ({
  tabActive,
  id,
  detailCompany,
  isLoadingDetail,
}: IDetailInfoProps) => {
  const [form] = Form.useForm()
  const [searchCountry, setSearchCountry] = useState<string>('')
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null)
  const [beneficiaryType, setBeneficiaryType] = useState('NEW')
  const [disableField, setDisableField] = useState(false)
  const { metadataCountries } = useMetadataCountries()

  const { mutate: fetchInfoByUserCode, isLoading } = useGetInfoByUserCode({
    onSuccess: (response: { data: { data: any } }) => {
      setInfoByUserCode(response?.data?.data)
      form.setFields([
        {
          name: 'smartUID',
          errors: undefined,
        },
      ])
    },
    onError: (error: { message: string; reason_code: string }) => {
      setInfoByUserCode(
        error?.reason_code === 'ERR00010'
          ? 'This SMART UID does not exist in SMART+'
          : error?.message
      )
      if (error?.reason_code === 'ERR00010') {
        form.setFields([
          {
            name: 'smartUID',
            errors: ['This SMART UID does not exist in SMART+'],
          },
        ])
      } else {
        form.setFields([
          {
            name: 'smartUID',
            errors: [error?.message || ''],
          },
        ])
      }
    },
  })

  const { mutate: updateCompany, isLoading: loadingUpdate } =
    useUpdateCompanyUser({
      onSuccess: (response: { data: { message: string } }) => {
        notification.success({
          message: response.data.message ||
            'Update Beneficiary & Remitter successfully!',
          placement: "bottom",
        });
        // navigate("/customers");
      },
      onError: (error: { message: string }) => {
        notification.error({
          message: error.message || 'Update Beneficiary & Remitter failed!',
          placement: "bottom",
        });
      },
    })

  const checkAllFieldsFilled = useCallback(() => {
    const values = form.getFieldsValue(requiredFields)
    return requiredFields.every((field) => !!values[field])
  }, [form])

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value)
  }

  const onSubmitInfo = (values: any) => {
    if (loadingUpdate || !id) return
    const body = {
      id: Number(id),
      fullName: values.companyName || '',
      name: values.nickName || '',
      directorFirstName: values.directorFirstName || '',
      directorLastName: values.directorLastName || '',
      address1: values.address1 || '',
      address2: values.address2 || '',
      city: values.city || '',
      stateProvince: values.stateProvince || '',
      country: values.companyOfficiallyRegistered || '',
      tinVatUid: values.tinVatUid || '',
      regNo: values.regNo || '',
      phoneNumber1: values.phoneNumber1 || '',
      email: values.email || '',
      smartUID: values.smartUID ? String(values.smartUID).trim() : null,
      website: values.website || '',
      postalCode: values?.postalCode || '',
      refUserId: infoByUserCode?.id ? infoByUserCode?.id : undefined,
      userId: detailCompany?.userId || undefined,
    }
    updateCompany(body)
  }

  useEffect(() => {
    if (!detailCompany) return
    if (tabActive !== 'info') {
      form.resetFields()
      setSearchCountry('')
      setInfoByUserCode(null)
      setDisableField(false)
      setIsAllFieldsFilled(false)
    } else {
      form.resetFields()
      form.setFieldsValue({
        companyName:
          detailCompany?.refUser?.fullName || detailCompany?.fullName || '',
        nickName: detailCompany?.name || '',
        directorFirstName: detailCompany?.directorFirstName || '',
        directorLastName: detailCompany?.directorLastName || '',
        address1:
          detailCompany?.refUser?.address || detailCompany?.address1 || '',
        address2: detailCompany?.address2 || '',
        city: detailCompany?.city || '',
        stateProvince: detailCompany?.stateProvince || '',
        companyOfficiallyRegistered:
          detailCompany?.refUser?.country || detailCompany?.country || '',
        tinVatUid: detailCompany?.tinVatUid || '',
        regNo: detailCompany?.refUser?.regNo || detailCompany?.regNo || '',
        phoneNumber1: detailCompany?.phoneNumber1 || '',
        email: detailCompany?.refUser?.email || detailCompany?.email || '',
        smartUID: detailCompany?.refUser?.smartUID || '',
        website:
          detailCompany?.refUser?.website || detailCompany?.website || '',
        postalCode: detailCompany?.postalCode || '',
      })
      setSearchCountry(
        detailCompany?.refUser?.country || detailCompany?.country
      )
      setInfoByUserCode(null)
      setDisableField(false)
      setIsAllFieldsFilled(checkAllFieldsFilled())
      if (detailCompany?.refUser?.smartUID) {
        setBeneficiaryType('FIND_ON_SMART')
        fetchInfoByUserCode(detailCompany?.refUser?.smartUID)
      } else {
        setBeneficiaryType('NEW')
      }
    }
  }, [detailCompany, tabActive, form, fetchInfoByUserCode, checkAllFieldsFilled])

  useEffect(() => {
    if (form.getFieldValue('smartUID')?.trim() === detailCompany?.smartUID) {
      form.setFieldsValue({
        companyName:
          detailCompany?.refUser?.fullName || detailCompany?.fullName || '',
        address1:
          detailCompany?.refUser?.address || detailCompany?.address1 || '',
        companyOfficiallyRegistered:
          detailCompany?.refUser?.country || detailCompany?.country || '',
        regNo: detailCompany?.refUser?.regNo || detailCompany?.regNo || '',
        email: detailCompany?.refUser?.email || detailCompany?.email || '',
        website:
          detailCompany?.refUser?.website || detailCompany?.website || '',
      })
      return
    }
    if (!infoByUserCode || typeof infoByUserCode === 'string') {
      setDisableField(false)
      return
    }
    setDisableField(true)
    form.setFieldsValue({
      companyName: infoByUserCode?.fullName,
      address1: infoByUserCode?.address,
      companyOfficiallyRegistered: infoByUserCode?.country,
      regNo: infoByUserCode?.regNo,
      email: infoByUserCode?.email,
      website: infoByUserCode?.website,
    })
  }, [
    infoByUserCode,
    form,
    detailCompany?.smartUID,
    detailCompany?.refUser?.fullName,
    detailCompany?.fullName,
    detailCompany?.refUser?.address,
    detailCompany?.address1,
    detailCompany?.refUser?.country,
    detailCompany?.country,
    detailCompany?.refUser?.regNo,
    detailCompany?.regNo,
    detailCompany?.refUser?.email,
    detailCompany?.email,
    detailCompany?.refUser?.website,
    detailCompany?.website,
  ])

  const handleBlur = useCallback(async () => {
    const code = form.getFieldValue('smartUID')
      ? form.getFieldValue('smartUID')?.trim()
      : ''

    if (!code) {
      setDisableField(false)
      form?.resetFields([
        'companyName',
        'nickName',
        'directorFirstName',
        'directorLastName',
        'address1',
        'address2',
        'city',
        'stateProvince',
        'companyOfficiallyRegistered',
        'tinVatUid',
        'regNo',
        'phoneNumber1',
        'email',
        'website',
        'postalCode',
      ])
      form.setFields([
        {
          name: 'smartUID',
          errors: undefined,
        },
      ])
      setInfoByUserCode(null)
      return
    }
    form?.resetFields([
      'companyName',
      'nickName',
      'directorFirstName',
      'directorLastName',
      'address1',
      'address2',
      'city',
      'stateProvince',
      'companyOfficiallyRegistered',
      'tinVatUid',
      'regNo',
      'phoneNumber1',
      'email',
      'website',
      'postalCode',
    ])
    await fetchInfoByUserCode(code)
  }, [form, fetchInfoByUserCode])

  return (
    <div
      className='detail-info-content'
      style={{ display: tabActive === 'info' ? 'block' : 'none' }}>
      {isLoadingDetail ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '400px',
          }}>
          <Spin size='large' className='custom-spin' />
        </div>
      ) : (
        <div
          style={{
            padding: '16px',
            maxWidth: '100%',
            backgroundColor: '#fff',
          }}>
          <Form
            form={form}
            layout='vertical'
            onFinish={onSubmitInfo}
            onValuesChange={() => {
              clearFieldErrorMessageOnChange(form)
              setIsAllFieldsFilled(checkAllFieldsFilled())
            }}>
            {beneficiaryType === 'FIND_ON_SMART' ? (
              <div
                className='form-info-content'
                style={{
                  paddingBottom: '0px',
                  paddingTop: '0px',
                }}>
                <Col
                  style={{
                    marginTop: '12px',
                    paddingBottom: '0px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
                  }}
                  xs={24}>
                  <Form.Item
                    label='SMART UID'
                    validateTrigger='onBlur'
                    name='smartUID'
                    rules={[
                      {
                        required: true,
                        message: 'This field is required',
                      },
                      {
                        max: 256,
                        message: 'Maximum 256 characters',
                        validateTrigger: 'onBlur',
                      },
                    ]}>
                    <Input
                      placeholder='Enter the code linked to your SMART account'
                      onBlur={handleBlur}
                      style={buttonStyle}
                      size='large'
                    />
                  </Form.Item>
                </Col>
              </div>
            ) : null}

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label='Name'
                  name='companyName'
                  rules={companyNameRules}
                  validateTrigger='onBlur'>
                  <Input
                    disabled={disableField}
                    placeholder='Enter the full legal name of the company'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Nickname'
                  name='nickName'
                  validateTrigger='onBlur'
                  rules={[...nameRules, onlyLatinCharsDigitsRule]}>
                  <Input
                    placeholder='Enter a short name or alias for internal use'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Reg No'
                  name='regNo'
                  validateTrigger='onBlur'
                  rules={companyRegistrationNumberRules}>
                  <Input
                    disabled={disableField}
                    placeholder='Enter the official company registration number'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='TIN'
                  name='tinVatUid'
                  validateTrigger='onBlur'
                  rules={tinRules}>
                  <Input
                    placeholder="Enter the company's Tax Identification Number"
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Address 1'
                  name='address1'
                  validateTrigger='onBlur'
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                      validateTrigger: 'onBlur',
                      whitespace: true
                    },
                    {
                      max: 256,
                      message: 'Maximum 256 characters',
                      validateTrigger: 'onBlur',
                    },
                  ]}>
                  <TextArea
                    disabled={disableField}
                    className='beneficiary-address'
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    placeholder='Enter the primary street address of the company'
                    style={buttonStyle}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Address 2'
                  name='address2'
                  validateTrigger='onBlur'
                  rules={[
                    {
                      max: 256,
                      message: 'Maximum 256 characters',
                      validateTrigger: 'onBlur',
                    },
                  ]}>
                  <TextArea
                    className='beneficiary-address'
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    placeholder='Additional address details (e.g., building, floor)'
                    style={buttonStyle}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='City'
                  name='city'
                  validateTrigger='onBlur'
                  rules={cityRules}>
                  <Input
                    placeholder='Enter the city where the company is located'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='State/Province'
                  name='stateProvince'
                  validateTrigger='onBlur'
                  rules={cityRules}>
                  <Input
                    placeholder='Enter the state or province of company location'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Country of incorporation'
                  name='companyOfficiallyRegistered'
                  validateTrigger='onBlur'
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}>
                  <Select
                    disabled={disableField}
                    showSearch
                    placeholder='Select the country where the company is officially registered'
                    suffixIcon={<DownOutlined />}
                    onChange={handleChangeCountry}
                    optionLabelProp='label'
                    className='select-country-beneficiary'
                    size='large'
                    style={buttonStyle}
                    filterOption={(input, option) => {
                      const label = option?.label
                      if (typeof label === 'string') {
                        return label.toLowerCase().includes(input.toLowerCase())
                      }
                      return false
                    }}>
                    {metadataCountries.map(
                      (item: { value: string; label: string }) => (
                        <Select.Option
                          key={item.value}
                          value={item.value}
                          label={item.label}>
                          <span
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              gap: 2,
                            }}>
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}>
                              {item.label}
                            </span>
                            {searchCountry === item.value && (
                              <CheckOutlined style={{ flexShrink: 0 }} />
                            )}
                          </span>
                        </Select.Option>
                      )
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Postal Code'
                  name='postalCode'
                  validateTrigger='onBlur'
                  rules={postalCodeRules}>
                  <Input
                    placeholder='Enter your postal code'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Director's First Name"
                  name='directorFirstName'
                  validateTrigger='onBlur'
                  rules={directorNameRules}>
                  <Input
                    placeholder='Enter the first name(s) of the company director'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Director's Last Name"
                  name='directorLastName'
                  validateTrigger='onBlur'
                  rules={directorNameRules}>
                  <Input
                    placeholder='Enter the last name (surname) of the company director'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Tel'
                  name='phoneNumber1'
                  validateTrigger='onBlur'
                  rules={phoneNumberRules}>
                  <Input
                    placeholder='Enter the official company phone number'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Email'
                  name='email'
                  validateTrigger='onBlur'
                  rules={[
                    ...emailRules,
                    {
                      required: true,
                      message: 'This field is required',
                      validateTrigger: 'onBlur',
                    },
                  ]}>
                  <Input
                    disabled={disableField}
                    placeholder='Enter the official company email address'
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label='Website'
                  name='website'
                  validateTrigger='onBlur'
                  rules={websiteRules}>
                  <Input
                    disabled={disableField}
                    placeholder="Enter the company's official website URL"
                    style={buttonStyle}
                    size='large'
                  />
                </Form.Item>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}>
              <Button
                key='save'
                type='primary'
                htmlType='submit'
                loading={loadingUpdate}
                className='smartFooterButtonXS'
                disabled={
                  !isAllFieldsFilled ||
                  isLoading ||
                  form.getFieldError('smartUID')?.length > 0 ||
                  (beneficiaryType === 'FIND_ON_SMART' && !infoByUserCode)
                }>
                Save
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  )
}

export default DetailInfo
